import React, { useState, useEffect } from 'react';
import { Tabs, Card, Button, Space, Timeline, Drawer, Form, Input, Select, Switch, Table, Tag, Tooltip, Row, Col, Statistic, InputNumber, Empty, Modal, Steps, Progress, Typography } from 'antd';
import { HistoryOutlined, SaveOutlined, RollbackOutlined, DeleteOutlined, PauseCircleOutlined, CheckCircleFilled, CloseCircleFilled, FileMarkdownOutlined, ApiOutlined, EyeOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { mockServices, mockRoutes, mockPlugins, mockRegions, mockOperationLogs } from '../mockData';
import type { Region, RegionTarget, Route, Plugin, OperationLog, Service } from '../mockData';
import MonitoringConfig from '../components/MonitoringConfig';
import RegionConfig from '../components/RegionConfig';
import GrayscaleRelease from '../components/GrayscaleRelease';
import PluginConfig from '../components/PluginConfig';
import RouteConfig from '../components/RouteConfig';
import type { RegionDeployStatus } from '../components/GrayscaleRelease';

const { TabPane } = Tabs;

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [service, setService] = useState<Service | undefined>();
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<OperationLog | null>(null);
  const [apiDetailModalVisible, setApiDetailModalVisible] = useState(false);
  const [selectedApi, setSelectedApi] = useState<Route | null>(null);
  const [deployStatus, setDeployStatus] = useState<RegionDeployStatus>({});

  // Find the service and its related data
  useEffect(() => {
    const foundService = mockServices.find(s => s.id === id);
    setService(foundService);
  }, [id]);

  const serviceRoutes = mockRoutes.filter(route => route.service.id === id);
  const servicePlugins = mockPlugins.filter(plugin => plugin.service.id === id);

  useEffect(() => {
    if (service) {
      form.setFieldsValue({
        name: service.name,
        description: service.name,
        host: service.host,
        port: service.port,
        protocol: service.protocol,
        path: serviceRoutes[0]?.paths[0] || '/api/v1/service',
        methods: ['GET', 'POST'],
        auth_enabled: servicePlugins.some(p => p.name === 'jwt' && p.enabled),
        rate_limiting_enabled: servicePlugins.some(p => p.name === 'rate-limiting' && p.enabled),
      });
    }
  }, [service, serviceRoutes, servicePlugins]);

  const renderBasicInfo = () => (
    <div className="basic-info">
      <div className="info-grid">
        <div className="info-row">
          <div className="info-label">服务名称</div>
          <div className="info-value">{service?.name || 'mdab-service-nsf-service'}</div>
        </div>
        <div className="info-row">
          <div className="info-label">服务类型</div>
          <div className="info-value">
            <Tag color="#00BCD4">HTTP</Tag>
          </div>
        </div>
        <div className="info-row">
          <div className="info-label">服务标识</div>
          <div className="info-value">{service?.id || 'mdab-service-nsf-service'}</div>
        </div>
        <div className="info-row">
          <div className="info-label">负责人</div>
          <div className="info-value">-</div>
        </div>
        <div className="info-row">
          <div className="info-label">备注信息</div>
          <div className="info-value">-</div>
        </div>
        <div className="info-row">
          <div className="info-label">发布状态</div>
          <div className="info-value">
            <Tag color="success">已发布</Tag>
          </div>
        </div>
        <div className="info-row">
          <div className="info-label">创建时间</div>
          <div className="info-value">2023-12-19 16:12:22</div>
        </div>
        <div className="info-row">
          <div className="info-label">修改时间</div>
          <div className="info-value">2023-12-19 16:35:34</div>
        </div>
        <div className="info-row" style={{ gridColumn: '1 / -1' }}>
          <div className="info-label">文档查看</div>
          <div className="info-value">
            <Space size="middle">
              <Button type="link" icon={<FileMarkdownOutlined />} style={{ padding: 0 }}>
                <Tag color="#1890ff" style={{ marginLeft: 4 }}>Markdown</Tag>
              </Button>
              <Button type="link" icon={<ApiOutlined />} style={{ padding: 0 }}>
                <Tag color="#52c41a" style={{ marginLeft: 4 }}>Swagger</Tag>
              </Button>
            </Space>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRegionConfig = () => {
    return <RegionConfig service={service} regions={mockRegions} />;
  };

  const renderRegionOverview = () => {
    // Calculate progress
    const totalRegions = mockRegions.length;
    const successfulDeployments = Object.values(deployStatus).filter(status => status.status === 'success').length;
    const progressPercentage = Math.round((successfulDeployments / totalRegions) * 100);

    // Group regions by geo
    const regionsByGeo = mockRegions.reduce((acc, region) => {
      const geo = region.geo;
      if (!acc[geo]) {
        acc[geo] = [];
      }
      acc[geo].push(region);
      return acc;
    }, {} as Record<string, Region[]>);

    // Calculate statistics for each region
    const getRegionStats = (region: Region) => {
      const totalTargets = region.targets.length;
      const healthyTargets = region.targets.filter(t => t.health_status === 'healthy').length;
      const activeTargets = region.targets.filter(t => t.node_status === 'active').length;
      return { totalTargets, healthyTargets, activeTargets };
    };

    const renderDeployStatus = (regionId: string) => {
      const status = deployStatus[regionId];
      if (!status?.status) return null;

      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '8px',
          background: status.status === 'success' ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${status.status === 'success' ? '#b7eb8f' : '#ffccc7'}`,
          borderRadius: '4px',
          padding: '4px 8px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: status.status === 'success' ? '#52c41a' : '#ff4d4f',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '8px'
          }}>
            {status.status === 'success' ? (
              <CheckCircleFilled style={{ color: 'white', fontSize: '14px' }} />
            ) : (
              <CloseCircleFilled style={{ color: 'white', fontSize: '14px' }} />
            )}
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column'
          }}>
            <span style={{ 
              color: status.status === 'success' ? '#52c41a' : '#ff4d4f', 
              fontWeight: 500, 
              fontSize: '13px',
              lineHeight: '1.2'
            }}>
              {status.status === 'success' ? '发布成功' : '发布失败'}
            </span>
            {status.timestamp && (
              <span style={{ color: '#8c8c8c', fontSize: '12px', lineHeight: '1.2' }}>
                {new Date(status.timestamp).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      );
    };

    return (
      <>
        <Card title="区域概览" className="region-overview-card" style={{ marginBottom: '24px' }}>
          {Object.keys(deployStatus).length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <Row align="middle" gutter={16}>
                <Col span={18}>
                  <Progress 
                    percent={progressPercentage}
                    status="success"
                    strokeColor="#52c41a"
                    format={() => `${successfulDeployments}/${totalRegions} 区域发布完成`}
                  />
                </Col>
                <Col span={6}>
                  <Typography.Text type="secondary">
                    灰度发布进度
                  </Typography.Text>
                </Col>
              </Row>
            </div>
          )}
          <Row gutter={[16, 16]}>
            {Object.entries(regionsByGeo).map(([geo, regions]) => (
              <Col key={geo} xs={24} sm={24} md={12} lg={12} xl={6}>
                <Card 
                  title={
                    <Space>
                      {geo === 'AsiaPacific' && 'AsiaPacific'}
                      {geo === 'Asia' && 'Asia'}
                      {geo === 'Europe' && 'Europe'}
                      {geo === 'NorthAmerica' && 'NorthAmerica'}
                    </Space>
                  }
                  size="small"
                  className="geo-card"
                >
                  {regions.map(region => {
                    const stats = getRegionStats(region);
                    return (
                      <Card.Grid key={region.id} style={{ width: '100%', padding: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Space wrap>
                              <span style={{ fontWeight: 500 }}>{region.name}</span>
                              {region.tags?.map((tag) => (
                                <Tag key={tag} color="orange">
                                  {tag}
                                </Tag>
                              ))}
                              {region.node_status === 'active' ? (
                                <Tag color="success">已启用</Tag>
                              ) : (
                                <Tag color="error">已禁用</Tag>
                              )}
                            </Space>
                            {renderDeployStatus(region.id)}
                          </Space>
                        </div>
                        <Row gutter={[8, 8]} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                          <Col style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px' }}>健康节点:</span>
                              <span style={{ 
                                fontSize: '14px', 
                                color: stats.healthyTargets === stats.totalTargets ? '#52c41a' : '#ff4d4f',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {stats.healthyTargets === stats.totalTargets ? <CheckCircleFilled /> : <CloseCircleFilled />}
                                {`${stats.healthyTargets}/${stats.totalTargets}`}
                              </span>
                            </div>
                          </Col>
                          <Col style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px' }}>启用节点:</span>
                              <span style={{ 
                                fontSize: '14px', 
                                color: stats.activeTargets === stats.totalTargets ? '#52c41a' : '#ff4d4f',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {stats.activeTargets === stats.totalTargets ? <CheckCircleFilled /> : <CloseCircleFilled />}
                                {`${stats.activeTargets}/${stats.totalTargets}`}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </Card.Grid>
                    );
                  })}
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </>
    );
  };

  const renderRouteConfig = () => {
    return (
      <RouteConfig
        serviceId={service?.id || ''}
        routes={serviceRoutes}
        plugins={mockPlugins}
        onPluginChange={(routeId, pluginName, enabled) => {
          console.log('Toggle route plugin:', routeId, pluginName, enabled);
        }}
      />
    );
  };

  const renderPluginConfig = () => {
    const servicePlugins = mockPlugins.filter(p => p.service.id === service?.id && !p.route);
    
    return (
      <PluginConfig
        plugins={servicePlugins}
        scope="service"
        serviceId={service?.id || ''}
        onPluginChange={(pluginName, enabled) => {
          console.log('Toggle service plugin:', pluginName, enabled);
        }}
      />
    );
  };

  const renderMonitoringConfig = () => {
    return <MonitoringConfig service={service} />;
  };

  const renderHistoryDrawer = () => (
    <Drawer
      title="操作日志"
      placement="right"
      width={600}
      onClose={() => setHistoryDrawerVisible(false)}
      open={historyDrawerVisible}
    >
      <Timeline>
        {mockOperationLogs.map((log) => (
          <Timeline.Item key={log.id}>
            <p>{log.description} [{new Date(log.operation_time).toLocaleString()}]</p>
            <p>操作人：{log.operator}</p>
            <Button 
              size="small" 
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedLog(log);
                setApiDetailModalVisible(true);
              }}
            >
              查看变更详情
            </Button>
          </Timeline.Item>
        ))}
      </Timeline>
    </Drawer>
  );

  const renderApiDetailModal = () => {
    if (!selectedLog) return null;

    return (
      <Modal
        title="变更详情"
        open={apiDetailModalVisible}
        onCancel={() => setApiDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 8 }}>基本信息</h4>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div>操作类型：{selectedLog.operation_type}</div>
            </Col>
            <Col span={12}>
              <div>操作人：{selectedLog.operator}</div>
            </Col>
            <Col span={24}>
              <div>操作时间：{new Date(selectedLog.operation_time).toLocaleString()}</div>
            </Col>
          </Row>
        </div>

        {selectedLog.api_request && (
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 8 }}>API 请求</h4>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
              <div>Method: {selectedLog.api_request.method}</div>
              <div>URL: {selectedLog.api_request.url}</div>
              <div>Headers:</div>
              <pre style={{ margin: '8px 0' }}>
                {JSON.stringify(selectedLog.api_request.headers, null, 2)}
              </pre>
              {selectedLog.api_request.body && (
                <>
                  <div>Body:</div>
                  <pre style={{ margin: '8px 0' }}>
                    {JSON.stringify(selectedLog.api_request.body, null, 2)}
                  </pre>
                </>
              )}
            </div>
          </div>
        )}

        {selectedLog.api_response && (
          <div>
            <h4 style={{ marginBottom: 8 }}>API 响应</h4>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4 }}>
              <div>Status: {selectedLog.api_response.status}</div>
              <div>Body:</div>
              <pre style={{ margin: '8px 0' }}>
                {JSON.stringify(selectedLog.api_response.body, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      console.log('Form values:', values);
      // Here you would typically save the data to your backend
    });
  };

  if (!service && id !== 'new') {
    return <div>Service not found</div>;
  }

  return (
    <div className="service-detail">
      <Card
        title={id === 'new' ? '新建服务' : '服务配置'}
        bordered={false}
        bodyStyle={{ padding: 0 }}
        style={{ background: 'transparent' }}
        extra={
          <Space>
            <Button icon={<HistoryOutlined />} onClick={() => setHistoryDrawerVisible(true)}>
              操作日志
            </Button>
          </Space>
        }
      >
        <Card 
          className="config-card" 
          bordered={false}
          style={{ background: 'transparent' }}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="基本信息" key="basic">
              {renderBasicInfo()}
              {renderRegionOverview()}
              <Card className="detail-tabs-card">
                <Tabs defaultActiveKey="api">
                  <TabPane tab="API管理" key="api">
                    {renderRouteConfig()}
                    <div style={{ marginTop: '24px' }}>
                      {renderPluginConfig()}
                    </div>
                  </TabPane>
                  <TabPane tab="灰度发布" key="grayscale">
                    <GrayscaleRelease 
                      deployStatus={deployStatus}
                      onDeployStatusChange={setDeployStatus}
                    />
                  </TabPane>
                </Tabs>
              </Card>
            </TabPane>
            <TabPane tab="区域配置" key="region">
              {renderRegionConfig()}
            </TabPane>
            <TabPane tab="监控告警" key="monitoring">
              {renderMonitoringConfig()}
            </TabPane>
          </Tabs>
        </Card>
      </Card>
      {renderHistoryDrawer()}
      {renderApiDetailModal()}
    </div>
  );
};

export default ServiceDetail; 